import React, { useState, useEffect, useRef } from 'react';
import { marketDiscoveryChat } from '../utils/api';
import { saveToStorage, loadFromStorage, StorageKeys, clearAllStorage } from '../utils/storage';

const ChatInterface = ({ userIdea, onMarketIdentified, onResetToStart }) => {
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [messageCount, setMessageCount] = useState(0);
  const [selectedBulletPoint, setSelectedBulletPoint] = useState(null);
  const messagesEndRef = useRef(null);
  const initialResponseCalled = useRef(false);

  useEffect(() => {
    const savedMessages = loadFromStorage(StorageKeys.CONVERSATION_HISTORY, []);
    if (savedMessages.length > 0) {
      setMessages(savedMessages);
    } else {
      // Let the first user message trigger the AI's expanded response
      const userMessage = {
        role: 'user',
        content: userIdea,
        timestamp: new Date().toISOString()
      };
      setMessages([userMessage]);

      // Trigger AI response with expansion - use ref to prevent double calls
      const timeoutId = setTimeout(() => {
        handleInitialAIResponse(userIdea);
      }, 500);

      // Cleanup function to cancel timeout if component unmounts
      return () => clearTimeout(timeoutId);
    }
  }, [userIdea]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const extractMarketFromResponse = (response) => {
    const marketPattern = /your core market appears to be:\s*([^.?!]+)/i;
    const match = response.match(marketPattern);
    return match ? match[1].trim() : null;
  };

  const handleSendMessage = async () => {
    if (!currentMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: currentMessage,
      timestamp: new Date().toISOString()
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setCurrentMessage('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await marketDiscoveryChat(currentMessage, messages);
      
      const assistantMessage = {
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString()
      };

      const updatedMessages = [...newMessages, assistantMessage];
      setMessages(updatedMessages);
      setMessageCount(prev => prev + 1);
      saveToStorage(StorageKeys.CONVERSATION_HISTORY, updatedMessages);

      const extractedMarket = extractMarketFromResponse(response);
      if (extractedMarket) {
        setTimeout(() => {
          onMarketIdentified(extractedMarket);
        }, 2000);
      }

    } catch (err) {
      setError(err.message || 'Failed to get response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleGoAhead = () => {
    const finalIdea = selectedBulletPoint || userIdea || "the current idea";
    onMarketIdentified(finalIdea);
  };

  const handleInitialAIResponse = async (idea) => {
    // Prevent duplicate calls
    if (initialResponseCalled.current) return;
    initialResponseCalled.current = true;

    setIsLoading(true);
    try {
      const response = await marketDiscoveryChat(idea, []);
      const assistantMessage = {
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString()
      };
      const updatedMessages = [{ role: 'user', content: idea, timestamp: new Date().toISOString() }, assistantMessage];
      setMessages(updatedMessages);
      setMessageCount(1);
      saveToStorage(StorageKeys.CONVERSATION_HISTORY, updatedMessages);
    } catch (err) {
      setError(err.message || 'Failed to get response. Please try again.');
      initialResponseCalled.current = false; // Reset on error so retry is possible
    } finally {
      setIsLoading(false);
    }
  };

  const parseBulletPoints = (content) => {
    const bulletRegex = /•\s*(.+)/g;
    const matches = [];
    let match;
    while ((match = bulletRegex.exec(content)) !== null) {
      matches.push(match[1].trim());
    }
    return matches;
  };

  const handleBulletPointSelect = (bulletPoint) => {
    setSelectedBulletPoint(bulletPoint);
  };

  const retryLastMessage = () => {
    if (messages.length >= 2 && messages[messages.length - 1].role === 'user') {
      const lastUserMessage = messages[messages.length - 1].content;
      setCurrentMessage(lastUserMessage);
      setMessages(messages.slice(0, -1));
      setError(null);
    }
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear all data and return to the start?')) {
      clearAllStorage();
      onResetToStart();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-200 p-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Market Discovery Chat</h2>
            <p className="text-sm text-gray-600 mt-1">
              Let's identify your core target market through conversation
            </p>
          </div>
          <button
            onClick={handleClearHistory}
            className="px-3 py-1 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors duration-200"
          >
            DEV: Clear History
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-hidden">
        <div className="max-w-4xl mx-auto h-full flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3xl p-4 rounded-2xl ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-900 shadow-sm'
                  }`}
                >
                  <div className="whitespace-pre-wrap leading-normal">
                    {message.role === 'assistant' && parseBulletPoints(message.content).length > 0
                      ? message.content.replace(/•\s*.+/g, '').trim().replace(/\n\s*\n/g, '\n')
                      : message.content.replace(/\n\s*\n/g, '\n')
                    }
                  </div>
                  
                  {/* Render bullet point selection for assistant messages */}
                  {message.role === 'assistant' && parseBulletPoints(message.content).length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Select a focus area to proceed:</p>
                      {parseBulletPoints(message.content).map((bulletPoint, bulletIndex) => (
                        <label
                          key={bulletIndex}
                          className={`flex items-start p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                            selectedBulletPoint === bulletPoint
                              ? 'bg-blue-50 border-2 border-blue-300'
                              : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          <input
                            type="radio"
                            name="bulletPoint"
                            checked={selectedBulletPoint === bulletPoint}
                            onChange={() => handleBulletPointSelect(bulletPoint)}
                            className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="text-sm">{bulletPoint}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  <div className={`text-xs mt-2 ${
                    message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-gray-600 text-sm">AI is thinking...</span>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="flex justify-center">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md">
                  <div className="flex items-center">
                    <div className="text-red-600 mr-2">⚠️</div>
                    <div>
                      <p className="text-red-800 font-medium">Error occurred</p>
                      <p className="text-red-600 text-sm">{error}</p>
                      <button
                        onClick={retryLastMessage}
                        className="text-red-600 hover:text-red-800 text-sm underline mt-1"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-gray-200 bg-white p-4">
            <div className="flex justify-center mb-4">
              <button
                onClick={handleGoAhead}
                className={`px-8 py-3 font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 ${
                  selectedBulletPoint 
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {selectedBulletPoint ? 'Go Ahead with Selected Focus' : 'Go Ahead with Current Idea'}
              </button>
            </div>
            {messageCount < 3 && (
              <div className="flex space-x-4">
                <div className="flex-1">
                  <textarea
                    value={currentMessage}
                    onChange={(e) => setCurrentMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your response... (Press Enter to send, Shift+Enter for new line)"
                    className="w-full p-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring focus:ring-blue-200 resize-none"
                    rows={3}
                    disabled={isLoading}
                  />
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!currentMessage.trim() || isLoading}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    !currentMessage.trim() || isLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                      Sending
                    </div>
                  ) : (
                    'Send'
                  )}
                </button>
              </div>
            )}
            {messageCount >= 3 && (
              <div className="text-center text-gray-600">
                <p className="mb-2">Chat limit reached. You can still go ahead with your current idea or refine it later.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;